import {
  Stack,
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  UnorderedList,
  ListItem,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { MdSend, MdLockOpen } from 'react-icons/md';
import { IoMdReturnLeft } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { ITitleCategory, ITitleItem, ITitleSubmission, ITitleSubmissionItem } from '../../../../services/hooks/TitleSubmissions/types';
import { useSubmitTitleSubmission, useReopenTitleSubmission, useScorePreview } from '../../../../services/hooks/TitleSubmissions/useSubmitTitleSubmission';
import { useAuditLogger } from '../../../../services/hooks/Audit/useAuditLogger';
import TitleCategoryAccordion from './TitleCategoryAccordion';

interface IOwnProps {
  submission: ITitleSubmission;
  onRefetch: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'RASCUNHO',
  submitted: 'SUBMETIDO',
  finalized: 'FINALIZADO',
};

const STATUS_COLOR: Record<string, string> = {
  draft: 'yellow',
  submitted: 'green',
  finalized: 'blue',
};

const TitleSubmissionContent: React.FC<IOwnProps> = ({ submission, onRefetch }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const auditLogger = useAuditLogger();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  const submitMutation = useSubmitTitleSubmission(submission.id);
  const reopenMutation = useReopenTitleSubmission(submission.id);
  const { data: scorePreview, isLoading: isLoadingScore } = useScorePreview(submission.id);

  const isDraft = submission.status === 'draft';

  const now = new Date();
  const periodStart = submission.titles_submission_start ? new Date(submission.titles_submission_start) : null;
  const periodEnd = submission.titles_submission_end ? new Date(submission.titles_submission_end) : null;
  const isPeriodOpen = (!periodStart || now >= periodStart) && (!periodEnd || now <= periodEnd);
  const isReadOnly = !isPeriodOpen;

  const categories = submission.table_version?.categories ?? [];
  const allTitleItems = categories.flatMap((cat: ITitleCategory) => cat.items);
  const totalMaxScore = categories.reduce((sum, cat: ITitleCategory) => sum + (Number(cat.max_score) || 0), 0);

  const itemsWithoutDoc = submission.items.filter((it: ITitleSubmissionItem) => {
    if (it.documents.length > 0) return false;
    const titleItem = allTitleItems.find((ti: ITitleItem) => ti.id === it.title_item);
    return titleItem ? titleItem.required_documents_json.length > 0 : false;
  });

  useEffect(() => {
    auditLogger.logPageAccess('Prova de Títulos', window.location.pathname);
  }, [submission.id]);

  const handleSubmit = async () => {
    try {
      await submitMutation.mutateAsync();
      await auditLogger.logStepAction(
        'step_completed',
        submission.step,
        'Prova de Títulos',
        submission.registration,
        'info',
        submission.id
      );
      toast({
        title: 'Submissão enviada com sucesso!',
        description: `Protocolo: ${submission.protocol ?? '—'}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onRefetch();
      onConfirmClose();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast({
        title: 'Erro ao enviar submissão',
        description: error?.response?.data?.detail || 'Ocorreu um erro ao finalizar o envio.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      onConfirmClose();
    }
  };

  const handleReopen = async () => {
    try {
      await reopenMutation.mutateAsync();
      toast({
        title: 'Submissão reaberta',
        description: 'Você pode editar e enviar novamente.',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
      onRefetch();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast({
        title: 'Erro ao reabrir',
        description: error?.response?.data?.detail || 'Não foi possível reabrir a submissão.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Stack spacing={4} pb={6} px={4}>
      {/* Header */}
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
        <Stack spacing={0}>
          <Flex align="center" gap={2} wrap="wrap">
            <Text fontWeight="bold" fontSize="xl" textTransform="uppercase">
              Formulário de envio de títulos - Prova de Títulos
            </Text>
            <Badge colorScheme={STATUS_COLOR[submission.status]} fontSize="sm" px={2} py={1}>
              {STATUS_LABEL[submission.status] ?? submission.status}
            </Badge>
          </Flex>
          {submission.selective_process_name && (
            <Text fontSize="sm" color="gray.500">
              {submission.selective_process_name}
            </Text>
          )}
          {submission.protocol && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              Protocolo: {submission.protocol}
            </Text>
          )}
        </Stack>
        <Button
          size="sm"
          leftIcon={<IoMdReturnLeft />}
          variant="outline"
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </Flex>

      {(periodStart || periodEnd) && (
        <Box p={3} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
          <Text fontSize="xs" fontWeight="bold" color="orange.700" textTransform="uppercase" mb={1}>
            Prazo de envio
          </Text>
          <Text fontSize="sm" color="orange.800">
            {periodStart ? periodStart.toLocaleString('pt-BR') : '—'}
            {' '}até{' '}
            {periodEnd ? periodEnd.toLocaleString('pt-BR') : '—'}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="orange.700" mt={1}>
            ⚠ Envie sua prova até o prazo indicado. Somente submissões com status{' '}
            <strong>SUBMETIDO</strong> e protocolo de envio confirmado serão consideradas na avaliação.
          </Text>
          <Text fontSize="sm" color="orange.700" mt={1}>
            Durante o período da prova você pode enviar e editar sua prova quantas vezes for necessário.
            Ao fim do período essa alteração não será possível.
          </Text>
        </Box>
      )}

      <Divider />

      {/* Alerta: submissão deve estar SUBMETIDA ao fim do prazo */}
      {isDraft && !isReadOnly && (
        <Alert status="warning" borderRadius="md" borderLeft="4px solid" borderLeftColor="orange.400">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Atenção: finalize o envio antes do prazo!</AlertTitle>
            <AlertDescription fontSize="xs">
              Sua prova de títulos <strong>somente será analisada pela banca</strong> se, ao encerramento do prazo de
              envio, a situação da prova estiver como <strong>SUBMETIDO</strong>. Submissões em rascunho não serão
              consideradas.
            </AlertDescription>
          </Box>
        </Alert>
      )}
      {isReadOnly && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">
              {periodStart && now < periodStart
                ? 'Período de envio não iniciado'
                : 'Período de envio encerrado'}
            </AlertTitle>
            <AlertDescription fontSize="xs">
              {periodEnd && now > periodEnd
                ? `O prazo encerrou em ${periodEnd.toLocaleString('pt-BR')}.`
                : periodStart && now < periodStart
                ? `O período de envio abre em ${periodStart.toLocaleString('pt-BR')}.`
                : 'Fora do período de envio.'}
              {' '}A submissão está em modo somente leitura.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Dados pós-envio */}
      {(submission.status === 'submitted' || submission.status === 'finalized') && (
        <Box borderWidth="1px" borderRadius="md" p={4} bg="green.50" borderColor="green.200">
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
            <Stack spacing={1}>
              <Text fontWeight="bold" fontSize="sm" color="green.700">
                Envio confirmado
              </Text>
              {submission.protocol && (
                <Text fontSize="sm">
                  <strong>Protocolo:</strong> {submission.protocol}
                </Text>
              )}
              {submission.submitted_at && (
                <Text fontSize="sm">
                  <strong>Data do envio:</strong>{' '}
                  {new Date(submission.submitted_at).toLocaleString('pt-BR')}
                </Text>
              )}
              {scorePreview?.total_declared != null && (
                <Text fontSize="sm">
                  <strong>Total declarado:</strong> {Number(scorePreview.total_declared).toFixed(2)} pts
                </Text>
              )}
            </Stack>
            {submission.status === 'submitted' && !isReadOnly && (
              <Stack spacing={1} align="flex-end">
                <Button
                  size="sm"
                  leftIcon={<MdLockOpen />}
                  colorScheme="orange"
                  variant="outline"
                  onClick={handleReopen}
                  isLoading={reopenMutation.isPending}
                >
                  Reabrir para edição
                </Button>
                <Text fontSize="xs" color="orange.600" textAlign="right" maxW="260px">
                  Ao reabrir para edição, o protocolo atual será invalidado. Um novo protocolo será gerado. Você pode editar sua prova até a data final de envio.
                </Text>
              </Stack>
            )}
          </Flex>
        </Box>
      )}

      {/* Categorias */}
      {categories.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">Nenhuma categoria disponível nesta tabela de títulos.</Text>
        </Alert>
      ) : (
        <Stack spacing={3}>
          <Box pt={6}>
            <Text fontWeight="bold" fontSize="md" textTransform="uppercase">
              Tabela de Títulos
            </Text>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Declare abaixo os títulos que você possui. Para cada item que se enquadrar no seu perfil,
              clique em "Declarar", informe a quantidade ou nível correspondente e anexe o comprovante.
              Apenas itens declarados serão considerados na pontuação final.{' '}
              <Text as="span" fontWeight="semibold" color="gray.700">
                O documento anexado em cada item deve estar em formato PDF, ser legível e ter tamanho máximo de 2 MB.
              </Text>
            </Text>
            <Divider mt={2} />
          </Box>

          {/* Score preview */}
          <Flex
            bg="blue.50"
            borderRadius="md"
            p={4}
            justify="space-between"
            align="center"
            wrap="wrap"
            gap={2}
            borderWidth="1px"
            borderColor="blue.200"
          >
            <Stack spacing={0}>
              <Text fontSize="sm" fontWeight="bold" color="blue.700" textTransform="uppercase">
                Resumo da Pontuação Total Declarada
              </Text>
              <Text fontSize="xs" color="blue.400">
                Pontuação máxima possível: {totalMaxScore.toFixed(2)} pts
              </Text>
            </Stack>
            {isLoadingScore ? (
              <Spinner size="sm" color="blue.500" />
            ) : (
              <Text fontSize="2xl" fontWeight="extrabold" color="blue.600">
                {scorePreview?.total_declared != null ? Number(scorePreview.total_declared).toFixed(2) : '—'} pts
              </Text>
            )}
          </Flex>

          {/* Alerta de itens sem documento (apenas rascunho) */}
          {isDraft && itemsWithoutDoc.length > 0 && (
            <Alert status="error" borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
              <Box>
                <AlertTitle fontSize="sm">Atenção! Você tem itens declarados com anexos pendentes:</AlertTitle>
                <AlertDescription fontSize="xs">
                  <UnorderedList mt={1} pl={2}>
                    {itemsWithoutDoc.map((it: ITitleSubmissionItem) => (
                      <ListItem key={it.id}>{it.title_item_name}</ListItem>
                    ))}
                  </UnorderedList>
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {categories
            .slice()
            .sort((a: ITitleCategory, b: ITitleCategory) => a.position - b.position)
            .map((category: ITitleCategory) => (
              <TitleCategoryAccordion
                key={category.id}
                category={category}
                submissionItems={submission.items}
                submissionId={submission.id}
                isDraft={isDraft && !isReadOnly}
              />
            ))}
        </Stack>
      )}

      {/* Botão de submissão */}
      {isDraft && !isReadOnly && (
        <Flex justify="flex-end">
          <Button
            colorScheme="green"
            leftIcon={<MdSend />}
            onClick={onConfirmOpen}
          >
            Finalizar e Enviar Prova
          </Button>
        </Flex>
      )}

      {/* Modal de confirmação de envio */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Confirmar envio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm">
              Após enviar, a submissão não poderá ser editada. Deseja continuar?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={onConfirmClose} isDisabled={submitMutation.isPending}>
              Cancelar
            </Button>
            <Button
              colorScheme="green"
              size="sm"
              ml={3}
              onClick={handleSubmit}
              isLoading={submitMutation.isPending}
            >
              Confirmar envio
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default TitleSubmissionContent;
